import * as React from 'react';
import { useState } from 'react';
import { cn } from '../../lib/utils';
import { Button } from '../button';
import { MarkdownContent } from '../markdown-content';
import type { ViewerSourceEditing } from './TextEditorView';
import { LoadingState } from '../shogun-spinner';

// Lazy: the CM6 editor is the heavy chunk. MarkdownView itself loads eagerly
// with FileViewerPane, so the editor import must stay dynamic — the raw side
// only pays for it when an editing host actually opens a markdown file.
const LazyTextEditorView = React.lazy(() =>
  import('./TextEditorView').then((m) => ({ default: m.TextEditorView })),
);

type ViewMode = 'rendered' | 'raw';

/**
 * Markdown viewer with a Rendered/Raw toggle. When an editing host passes
 * `editing`, the raw side becomes the CM6 editor with
 * the host's buffer wiring and `content` is the buffer AT SYNC POINTS — so the
 * rendered preview reflects saved-or-synced edits, never disk. Dirty pins the
 * toggle to raw with a "save to preview" hint. Without `editing` the
 * component behaves exactly as before (read-only, `<pre>` raw side).
 */
export function MarkdownView({
  content,
  filename,
  editing,
}: {
  content: string;
  /** Required for language selection when `editing` hosts the source editor. */
  filename?: string;
  editing?: ViewerSourceEditing;
}) {
  const [mode, setMode] = useState<ViewMode>('rendered');
  // Dirty pins the toggle to source: unsaved edits must stay visible in
  // the surface that owns them until saved.
  const pinnedToRaw = editing?.dirty === true;
  const effectiveMode: ViewMode = pinnedToRaw ? 'raw' : mode;

  return (
    <div className={cn('rounded-md text-sm', editing && 'flex h-full min-h-0 flex-col')}>
      <div className="flex items-center justify-end gap-1 border-b px-3 py-1.5">
        {pinnedToRaw && (
          <span className="mr-auto text-xs text-muted-foreground" data-testid="save-to-preview-hint">
            Save to preview
          </span>
        )}
        <Button
          type="button"
          variant={effectiveMode === 'rendered' ? 'secondary' : 'ghost'}
          size="sm"
          onClick={() => setMode('rendered')}
          disabled={pinnedToRaw}
          className="h-7 px-2 text-xs"
        >
          Rendered
        </Button>
        <Button
          type="button"
          variant={effectiveMode === 'raw' ? 'secondary' : 'ghost'}
          size="sm"
          onClick={() => setMode('raw')}
          className="h-7 px-2 text-xs"
        >
          Raw
        </Button>
      </div>
      <div className={cn('p-3', editing && 'min-h-0 flex-1 overflow-auto')}>
        {effectiveMode === 'rendered' ? (
          <MarkdownContent content={content} />
        ) : editing ? (
          <React.Suspense
            fallback={<LoadingState label="Loading editor…" />}
          >
            <LazyTextEditorView
              content={content}
              filename={filename ?? 'file.md'}
              readOnly={editing.readOnly}
              onDocChanged={editing.onDocChanged}
              getTextRef={editing.getTextRef}
              onSave={editing.onSave}
              className="h-full"
            />
          </React.Suspense>
        ) : (
          <pre className={cn('overflow-x-auto whitespace-pre-wrap font-mono text-xs')}>{content}</pre>
        )}
      </div>
    </div>
  );
}
